import * as React from 'react';
import { cx } from '../tone';

export type ResilienceKind = 'empty' | 'offline' | 'retry';

export interface ResilienceStateProps {
  /** Which calm state to show. @default 'empty' */
  kind?: ResilienceKind;
  /** Title (overrides the per-kind default). */
  title?: React.ReactNode;
  /** Detail line (overrides the per-kind default). */
  detail?: React.ReactNode;
  /** Extra classes on the root. */
  className?: string;
  /** Inline style on the root. */
  style?: React.CSSProperties;
  /**
   * ARIA role on the root. Defaults to `'status'` so the standalone atom is a
   * polite live region. Pass `null` to **omit** the role entirely when an
   * ancestor already owns the live region (e.g. when `DiscoverEmptyState`
   * wraps this for visuals and announces the message itself — avoids nested
   * live regions and the WEB-289 per-tick re-announce). `null` (not
   * `undefined`) is the explicit neutralize sentinel so omitting the prop
   * keeps the standalone-atom default. @default 'status'
   */
  role?: React.AriaRole | null;
  /**
   * `aria-live` politeness on the root. Defaults to `'polite'` to match the
   * `'status'` role. Pass `'off'` to silence the atom's own region when an
   * ancestor owns announcements. @default 'polite'
   */
  ariaLive?: React.AriaAttributes['aria-live'];
}

const RESILIENCE_DEFAULTS: Record<ResilienceKind, { title: string; detail: string }> = {
  empty: { title: 'No featured events right now', detail: 'Check back soon' },
  offline: { title: "You're offline", detail: 'Showing your saved footprint' },
  retry: { title: "Couldn't load", detail: 'Retrying' },
};

/**
 * `ResilienceState` — calm, accurate empty / offline / retry rows. No apology
 * theatre: empty gets a faint pulsing O, retry a quiet teal ring with the
 * attempt, offline a slow rose beat. Tone matches the voice — a knowing local,
 * never an alarm.
 */
export function ResilienceState({
  kind = 'empty',
  title,
  detail,
  className,
  style,
  role = 'status',
  ariaLive = 'polite',
}: ResilienceStateProps): React.ReactElement {
  const defaults = RESILIENCE_DEFAULTS[kind];
  return (
    <div
      className={cx('lm-state', `lm-state--${kind}`, className)}
      style={style}
      // `null` is the explicit neutralize sentinel → omit the attribute.
      role={role ?? undefined}
      aria-live={ariaLive}
    >
      <span className="lm-state__ico" aria-hidden="true">
        {kind === 'retry' ? <span className="lm-ring-mini" /> : <span className="lm-od" />}
      </span>
      <span className="lm-state__label">
        <b>{title ?? defaults.title}</b>
        <span>{detail ?? defaults.detail}</span>
      </span>
    </div>
  );
}
